import { useEffect, useState } from "react";
import { BookOpen, Feather, Heart, Megaphone, Mic, Music, PenTool, Star } from "lucide-react";
import { http } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export const TILE_COLORS = {
  aqua: "bg-aqua text-ink", passion: "bg-passion text-ink", lav: "bg-lav text-ink", tang: "bg-tang text-ink",
  tea: "bg-tea text-shell", mustard: "bg-mustard text-ink", quartz: "bg-quartz text-ink", char: "bg-char text-shell",
};
const AUTO = Object.keys(TILE_COLORS);
export const ICONS = { mic: Mic, pen: PenTool, book: BookOpen, megaphone: Megaphone, feather: Feather, heart: Heart, star: Star, music: Music };
const BY_CAT = { "Poetry Club": "mic", "Open Mic": "mic", Workshop: "pen", Literacy: "book", "Def Street": "megaphone" };

const Tile = ({ e, i }) => {
  const d = new Date(`${e.date}T12:00:00`);
  const color = TILE_COLORS[e.color] || TILE_COLORS[AUTO[i % AUTO.length]];
  const Icon = ICONS[e.icon] || ICONS[BY_CAT[e.category]] || Feather;
  return (
    <article className={`group relative aspect-square overflow-hidden ${color}`} data-testid={`schedule-tile-${e.id}`}>
      <Icon className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 w-[58%] h-[58%] opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-[opacity,transform] duration-500" strokeWidth={1.25} />
      <div className="relative flex items-start gap-1 p-3 sm:p-4">
        <span className="vertical font-display uppercase text-xl sm:text-3xl lg:text-4xl leading-none tracking-tight">{d.toLocaleDateString("en-US", { month: "short" })}</span>
        <span className="font-display text-[4.2rem] sm:text-[6.5rem] lg:text-[8rem] leading-[0.78] tracking-tighter">{String(d.getDate()).padStart(2, "0")}</span>
      </div>
      <div className="absolute bottom-0 inset-x-0 h-[36%] bg-black/15 flex flex-col justify-end items-center text-center px-2 pb-3 sm:pb-4">
        <p className="uppercase text-[10px] sm:text-sm tracking-wide font-medium leading-tight line-clamp-2">{e.title}</p>
        {e.presenter && <p className="uppercase text-[10px] sm:text-sm font-bold tracking-wide mt-0.5 line-clamp-1">{e.presenter}</p>}
        {e.time && <p className="font-mono text-[9px] sm:text-[11px] opacity-75 mt-0.5 hidden sm:block">{e.time}</p>}
      </div>
    </article>
  );
};

const TitleTile = ({ s }) => (
  <div className="aspect-square bg-ink text-shell flex flex-col justify-center gap-2 p-3 sm:p-5" data-testid="schedule-title-tile">
    <span className="self-start bg-shell text-ink font-display text-xs sm:text-xl px-2 py-0.5" data-testid="schedule-label">{s.schedule_label}</span>
    <span className="font-display uppercase text-2xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight" data-testid="schedule-title">{s.schedule_title}</span>
    <span className="border-t-2 border-shell pt-2 uppercase tracking-[0.25em] text-[9px] sm:text-sm font-light">{s.schedule_subtitle}</span>
  </div>
);

export const ScheduleBlock = ({ category, limit = 11 }) => {
  const { settings: s } = useSite() || {};
  const [items, setItems] = useState(null);
  useEffect(() => {
    http.get("/public/events?include_past=true").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (!s || items === null) return <div className="aspect-[3/2] bg-ink2 animate-pulse" />;
  const list = (category ? items.filter((e) => category.includes(e.category)) : items).slice(0, limit);
  return (
    <div className="bg-ink p-2 sm:p-4" data-testid="schedule-block">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
        <TitleTile s={s} />
        {list.map((e, i) => <Tile key={e.id} e={e} i={i} />)}
      </div>
      {!list.length && <p className="text-center text-shell/70 py-6" data-testid="schedule-empty">New dates are being scheduled — join the newsletter to hear first.</p>}
      {s.schedule_note && <p className="text-center uppercase tracking-[0.15em] text-xs sm:text-sm font-light text-shell/85 pt-6 pb-2 max-w-3xl mx-auto" data-testid="schedule-note">{s.schedule_note}</p>}
    </div>
  );
};
